import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import DictableTextarea from '@/components/DictableTextarea';
import { autoDecodeCF } from '@/lib/cfDecoder';
import { toast } from 'sonner';
import DatePickerField from '@/components/DatePickerField';

const tipAssistenza = {
  non_autosufficiente: 'Non Autosufficiente',
  parzialmente_autosufficiente: 'Parzialmente Autosufficiente',
  autosufficiente: 'Autosufficiente',
  demenza: 'Demenza',
  sollievo: 'Ricovero di Sollievo',
};

const SERVIZI_DISPONIBILI = [
  { nome: 'Fisioterapia individuale', prezzo: 150 },
  { nome: 'Logopedia', prezzo: 120 },
  { nome: 'Trasporto sanitario', prezzo: 200 },
  { nome: 'Parrucchiere/Barbiere', prezzo: 25 },
  { nome: 'Podologia', prezzo: 30 },
  { nome: 'Assistenza notturna dedicata', prezzo: 750 },
  { nome: 'Camera singola supplemento', prezzo: 450 },
  { nome: 'Lavanderia personale', prezzo: 50 },
];

const emptyForm = {
  nome_candidato: '', cognome_candidato: '', data_nascita: '', codice_fiscale: '',
  telefono: '', email: '', referente_nome: '', referente_telefono: '',
  tipologia_soggiorno: 'permanente', tipologia_camera: 'doppia', area_richiesta: '',
  retta_mensile_base: 2850, servizi_accessori: SERVIZI_DISPONIBILI.map(s => ({ ...s, attivo: false })),
  durata_prevista_giorni: 30, note: '', priorita: 'media',
  tipologia_assistenza: '', provenienza: '', motivo_richiesta: '', patologie_note: '',
  autonomia_deambulazione: '', autonomia_alimentazione: '', stato_cognitivo: '',
};

/**
 * Uses a ref-based approach for text inputs to avoid re-rendering
 * the entire form on every keystroke. Only Select/Switch changes
 * trigger re-renders (which is needed to update the UI).
 */
const PreventivoFormBody = memo(function PreventivoFormBody({ editingPrev, onSave, onCancel }) {
  const formRef = useRef({ ...emptyForm });
  // Only these trigger re-renders (for visual updates)
  const [servizi, setServizi] = useState(SERVIZI_DISPONIBILI.map(s => ({ ...s, attivo: false })));
  const [rettaBase, setRettaBase] = useState(2850);
  const [selects, setSelects] = useState({
    tipologia_soggiorno: 'permanente', tipologia_camera: 'doppia',
    area_richiesta: '', tipologia_assistenza: '', stato_cognitivo: '',
  });
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (editingPrev) {
      const sv = (editingPrev.servizi_accessori && editingPrev.servizi_accessori.length > 0)
        ? SERVIZI_DISPONIBILI.map(sd => {
            const existing = editingPrev.servizi_accessori.find(s => s.nome === sd.nome);
            return existing ? { ...existing } : { ...sd, attivo: false };
          })
        : SERVIZI_DISPONIBILI.map(s => ({ ...s, attivo: false }));
      const base = editingPrev.retta_mensile_base || editingPrev.retta_mensile || (editingPrev.retta_giornaliera ? editingPrev.retta_giornaliera * 30 : 2850);
      formRef.current = {
        nome_candidato: editingPrev.nome_candidato || '', cognome_candidato: editingPrev.cognome_candidato || '',
        data_nascita: editingPrev.data_nascita || '', codice_fiscale: editingPrev.codice_fiscale || '',
        telefono: editingPrev.telefono || '', email: editingPrev.email || '',
        referente_nome: editingPrev.referente_nome || '', referente_telefono: editingPrev.referente_telefono || '',
        tipologia_soggiorno: editingPrev.tipologia_soggiorno || 'permanente', tipologia_camera: editingPrev.tipologia_camera || 'doppia',
        area_richiesta: editingPrev.area_richiesta || '',
        retta_mensile_base: base, servizi_accessori: sv,
        durata_prevista_giorni: editingPrev.durata_prevista_giorni || 30, note: editingPrev.note || '',
        priorita: editingPrev.priorita || 'media', tipologia_assistenza: editingPrev.tipologia_assistenza || '',
        provenienza: editingPrev.provenienza || '', motivo_richiesta: editingPrev.motivo_richiesta || '',
        patologie_note: editingPrev.patologie_note || '', autonomia_deambulazione: editingPrev.autonomia_deambulazione || '',
        autonomia_alimentazione: editingPrev.autonomia_alimentazione || '', stato_cognitivo: editingPrev.stato_cognitivo || '',
      };
      setServizi(sv);
      setRettaBase(base);
      setSelects({
        tipologia_soggiorno: editingPrev.tipologia_soggiorno || 'permanente',
        tipologia_camera: editingPrev.tipologia_camera || 'doppia',
        area_richiesta: editingPrev.area_richiesta || '',
        tipologia_assistenza: editingPrev.tipologia_assistenza || '',
        stato_cognitivo: editingPrev.stato_cognitivo || '',
        priorita: editingPrev.priorita || 'media',
      });
    } else {
      formRef.current = { ...emptyForm };
      setServizi(SERVIZI_DISPONIBILI.map(s => ({ ...s, attivo: false })));
      setRettaBase(2850);
      setSelects({ tipologia_soggiorno: 'permanente', tipologia_camera: 'doppia', area_richiesta: '', tipologia_assistenza: '', stato_cognitivo: '', priorita: 'media' });
    }
    setFormKey(k => k + 1);
  }, [editingPrev]);

  const rettaTotale = useCallback(() => {
    const base = parseFloat(rettaBase) || 0;
    const serviziTot = servizi.filter(s => s.attivo).reduce((sum, s) => sum + (parseFloat(s.prezzo) || 0), 0);
    return (base + serviziTot).toFixed(2);
  }, [rettaBase, servizi]);

  const serviziTotale = servizi.filter(s => s.attivo).reduce((sum, s) => sum + (parseFloat(s.prezzo) || 0), 0);

  const toggleServizio = (idx) => {
    setServizi(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], attivo: !next[idx].attivo };
      formRef.current.servizi_accessori = next;
      return next;
    });
  };

  const updateServizioPrezzo = (idx, prezzo) => {
    setServizi(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], prezzo: parseFloat(prezzo) || 0 };
      formRef.current.servizi_accessori = next;
      return next;
    });
  };

  const handleSelectChange = (field, value) => {
    formRef.current[field] = value;
    setSelects(prev => ({ ...prev, [field]: value }));
  };

  const handleRettaBaseChange = (e) => {
    const v = e.target.value;
    formRef.current.retta_mensile_base = v;
    setRettaBase(v);
  };

  const handleSave = () => {
    // Gather latest values from ref
    const data = { ...formRef.current, servizi_accessori: servizi };
    onSave(data);
  };

  // Text input: just update ref, NO re-render
  const textHandler = (field) => (e) => { formRef.current[field] = e.target.value; };

  return (
    <div className="space-y-4" key={formKey}>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Dati Candidato</p>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Nome *</Label><Input data-testid="prev-nome" defaultValue={formRef.current.nome_candidato} onChange={textHandler('nome_candidato')} /></div>
        <div><Label className="text-xs">Cognome *</Label><Input data-testid="prev-cognome" defaultValue={formRef.current.cognome_candidato} onChange={textHandler('cognome_candidato')} /></div>
        <div><Label className="text-xs">Data Nascita</Label><DatePickerField value={formRef.current.data_nascita} onChange={v => (textHandler('data_nascita'))({ target: { value: v } })} /></div>
        <div><Label className="text-xs">Codice Fiscale</Label><Input defaultValue={formRef.current.codice_fiscale} onChange={e => {
          const val = e.target.value.toUpperCase();
          formRef.current.codice_fiscale = val;
          autoDecodeCF(val, (r) => {
            if (r.data_nascita) formRef.current.data_nascita = r.data_nascita;
            toast.success(`CF decodificato: ${r.luogo_nascita || ''}${r.eta != null ? `, ${r.eta} anni` : ''}`);
          });
        }} /></div>
        <div><Label className="text-xs">Telefono</Label><Input defaultValue={formRef.current.telefono} onChange={textHandler('telefono')} /></div>
        <div><Label className="text-xs">Email</Label><Input type="email" defaultValue={formRef.current.email} onChange={textHandler('email')} /></div>
        <div><Label className="text-xs">Referente</Label><Input defaultValue={formRef.current.referente_nome} onChange={textHandler('referente_nome')} placeholder="Nome referente" /></div>
        <div><Label className="text-xs">Tel. Referente</Label><Input defaultValue={formRef.current.referente_telefono} onChange={textHandler('referente_telefono')} /></div>
      </div>

      <Separator />
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Valutazione Pre-Ingresso</p>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Tipologia Assistenza</Label>
          <Select value={selects.tipologia_assistenza} onValueChange={v => handleSelectChange('tipologia_assistenza', v)}><SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
            <SelectContent>{Object.entries(tipAssistenza).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-xs">Stato Cognitivo</Label>
          <Select value={selects.stato_cognitivo} onValueChange={v => handleSelectChange('stato_cognitivo', v)}><SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
            <SelectContent><SelectItem value="integro">Integro</SelectItem><SelectItem value="lieve_decadimento">Lieve Decadimento</SelectItem><SelectItem value="moderato_decadimento">Moderato</SelectItem><SelectItem value="grave_decadimento">Grave</SelectItem></SelectContent></Select></div>
        <div><Label className="text-xs">Deambulazione</Label><Input defaultValue={formRef.current.autonomia_deambulazione} onChange={textHandler('autonomia_deambulazione')} placeholder="Autonomo, con ausili, allettato..." /></div>
        <div><Label className="text-xs">Alimentazione</Label><Input defaultValue={formRef.current.autonomia_alimentazione} onChange={textHandler('autonomia_alimentazione')} placeholder="Autonomo, assistito, SNG/PEG..." /></div>
      </div>
      <div><Label className="text-xs">Patologie Note</Label><DictableTextarea defaultValue={formRef.current.patologie_note} onChange={textHandler('patologie_note')} rows={2} placeholder="Patologie principali..." /></div>
      <div><Label className="text-xs">Provenienza</Label><Input defaultValue={formRef.current.provenienza} onChange={textHandler('provenienza')} placeholder="Domicilio, Ospedale, Altra RSA..." /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Priorita Ammissione</Label>
          <Select value={selects.priorita || 'media'} onValueChange={v => handleSelectChange('priorita', v)}><SelectTrigger data-testid="prev-priorita"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="urgente">Urgente</SelectItem>
              <SelectItem value="alta">Alta</SelectItem>
              <SelectItem value="media">Media</SelectItem>
              <SelectItem value="bassa">Bassa</SelectItem>
            </SelectContent></Select></div>
        <div><Label className="text-xs">Motivo Richiesta</Label><Input defaultValue={formRef.current.motivo_richiesta} onChange={textHandler('motivo_richiesta')} placeholder="Peggioramento clinico, impossibilita familiare..." /></div>
      </div>

      <Separator />
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Dettagli Economici</p>
      <div className="grid grid-cols-3 gap-3">
        <div><Label className="text-xs">Soggiorno</Label>
          <Select value={selects.tipologia_soggiorno} onValueChange={v => handleSelectChange('tipologia_soggiorno', v)}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="permanente">Permanente</SelectItem><SelectItem value="temporaneo">Temporaneo</SelectItem><SelectItem value="sollievo">Sollievo</SelectItem></SelectContent></Select></div>
        <div><Label className="text-xs">Camera</Label>
          <Select value={selects.tipologia_camera} onValueChange={v => handleSelectChange('tipologia_camera', v)}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="singola">Singola</SelectItem><SelectItem value="doppia">Doppia</SelectItem></SelectContent></Select></div>
        <div><Label className="text-xs">Area Richiesta</Label>
          <Select value={selects.area_richiesta || '_none'} onValueChange={v => handleSelectChange('area_richiesta', v === '_none' ? '' : v)}><SelectTrigger><SelectValue placeholder="Nessuna preferenza" /></SelectTrigger>
            <SelectContent><SelectItem value="_none">Nessuna preferenza</SelectItem><SelectItem value="verde">Area Verde</SelectItem><SelectItem value="gialla">Area Gialla</SelectItem></SelectContent></Select></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Retta Mensile Base (EUR)</Label><Input type="number" step="0.01" data-testid="prev-retta" value={rettaBase} onChange={handleRettaBaseChange} /></div>
        <div><Label className="text-xs">Durata Prevista (giorni)</Label><Input type="number" defaultValue={formRef.current.durata_prevista_giorni} onChange={textHandler('durata_prevista_giorni')} /></div>
      </div>

      <div>
        <Label className="text-xs font-semibold">Servizi Accessori</Label>
        <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-slate-50">
          {servizi.map((s, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <Switch checked={s.attivo} onCheckedChange={() => toggleServizio(idx)} data-testid={`servizio-toggle-${idx}`} />
              <span className={`text-xs flex-1 ${s.attivo ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>{s.nome}</span>
              <Input type="number" step="0.01" className="w-20 h-7 text-xs text-right" value={s.prezzo} onChange={e => updateServizioPrezzo(idx, e.target.value)} disabled={!s.attivo} />
              <span className="text-[10px] text-slate-400">EUR/mese</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
        <p className="text-[10px] text-emerald-600 uppercase font-semibold tracking-wider">Retta Mensile Complessiva</p>
        <p className="text-2xl font-bold text-emerald-700" data-testid="retta-mensile-preview">EUR {rettaTotale()}</p>
        <p className="text-[10px] text-slate-500 mt-1">
          Retta base EUR {parseFloat(rettaBase || 0).toFixed(2)} +
          Servizi EUR {serviziTotale.toFixed(2)}/mese
        </p>
      </div>

      <div><Label className="text-xs">Note</Label><DictableTextarea defaultValue={formRef.current.note} onChange={textHandler('note')} rows={2} /></div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Annulla</Button>
        <Button data-testid="save-preventivo" onClick={handleSave} className="bg-brand hover:bg-brand-dark text-white">
          {editingPrev ? 'Aggiorna Preventivo' : 'Crea Preventivo'}
        </Button>
      </div>
    </div>
  );
});

export default PreventivoFormBody;
export { SERVIZI_DISPONIBILI, emptyForm as emptyPrevForm };
